// src/components/forms/base/types.ts

import { type ReactNode } from 'react'

// ==============================
// FORM APPEARANCE & STYLING
// ==============================

/**
 * Defines the visual style variants available for form components.
 * @type {('floating' | 'embedded' | 'split' | 'card' | 'specialized' | 'step' | 'popup' | 'corner' | 'hero')}
 */
export type FormVariant = 'floating' | 'embedded' | 'split' | 'card' | 'specialized' | 'step' | 'popup' | 'corner' | 'hero' | 'gated'

/**
 * Defines the layout options for form components.
 * @type {('inline' | 'stacked' | 'split' | 'fullWidth')}
 */
export type FormLayout = 'inline' | 'stacked' | 'split' | 'fullWidth'

/**
 * Defines the size options for form components.
 * @type {('sm' | 'md' | 'lg' | 'xl')}
 */
export type FormSize = 'sm' | 'md' | 'lg' | 'xl'

/**
 * Defines the theme options for form components.
 * @type {('light' | 'dark' | 'brand' | 'custom')}
 */
export type FormTheme = 'light' | 'dark' | 'brand' | 'custom'

/**
 * Valid template aliases for email templates
 * @type {readonly ['welcome', 'gated-resource-1', 'gated-content-2', 'resource-center-tool-results', 'newsletter-minimal', 'newsletter-standard', 'early-access-application']}
 */
export const VALID_TEMPLATE_ALIASES = [
  'welcome', 
  'gated-resource-1', 
  'gated-content-2', 
  'resource-center-tool-results', 
  'newsletter-minimal', 
  'newsletter-standard',
  'early-access-application'
] as const

export type ValidTemplateAlias = typeof VALID_TEMPLATE_ALIASES[number]

/**
 * Base template model containing common fields used across all email templates.
 * @interface
 * @property {string} logo_url - URL for company/product logo
 * @property {string} product_name - Name of the product
 * @property {string} product_url - URL to the product
 * @property {string} [name] - Optional recipient name
 * @property {string} support_email - Support email address
 * @property {string} sender_name - Name of the sender
 * @property {string} company_name - Company name
 * @property {string} company_address - Company physical address
 * @property {string} [action_url] - Optional URL for primary CTA
 * @property {string} [login_url] - Optional URL for login
 * @property {string} [username] - Optional username
 * @property {string} [trial_length] - Optional trial period length
 * @property {string} [trial_start_date] - Optional trial start date
 * @property {string} [trial_end_date] - Optional trial end date
 * @property {string} [live_chat_url] - Optional live chat URL
 * @property {string} [help_url] - Optional help/documentation URL
 */
export interface BaseTemplateModel {
  /** URL for company/product logo */
  logo_url: string
  /** Name of the product */
  product_name: string
  /** URL to the product */
  product_url: string
  /** Optional recipient name */
  name?: string
  /** Support email address */
  support_email: string
  /** Name of the sender */
  sender_name: string
  /** Company name */
  company_name: string
  /** Company physical address */
  company_address: string
  /** Optional URL for primary CTA */
  action_url?: string
  /** Optional URL for login */
  login_url?: string
  /** Optional username */
  username?: string
  /** Optional trial period length */
  trial_length?: string
  /** Optional trial start date */
  trial_start_date?: string
  /** Optional trial end date */
  trial_end_date?: string
  /** Optional live chat URL */
  live_chat_url?: string
  /** Optional help/documentation URL */
  help_url?: string
}

/**
 * Template model for gated content emails, extending the base template.
 * @interface
 * @extends {BaseTemplateModel}
 * @property {string} preheader - Email preview text
 * @property {string} resource_icon_url - URL for resource icon/image
 * @property {string} resource_name - Name of the gated resource
 * @property {string} resource_link - URL to access the resource
 * @property {string} [resource_preview_link] - Optional URL to preview the resource
 * @property {string} [resource_share_url] - Optional canonical URL used when generating share links
 * @property {string} resource_description - Description of the resource
 * @property {string} [resource_type_label] - Flexible label for the resource family (Template, Calculator results, Playbook, etc.)
 * @property {string} [resource_cta_label] - Flexible primary CTA copy
 * @property {string} [resource_access_heading] - Flexible heading above the access button
 * @property {string} [resource_delivery_note] - Flexible note shown above the access button
 * @property {string} [resource_usage_tip] - Flexible suggested next step copy
 * @property {string} [resource_usage_heading] - Flexible suggested next step heading
 * @property {string} [related_resources_heading] - Flexible heading for related resources
 * @property {string} [share_prompt] - Flexible social/share prompt
 * @property {string} [related_resource_1_link] - Optional URL for first related resource
 * @property {string} [related_resource_1_name] - Optional name for first related resource
 * @property {string} [related_resource_2_link] - Optional URL for second related resource
 * @property {string} [related_resource_2_name] - Optional name for second related resource
 * @property {string} [twitter_share_url] - Optional Twitter share URL
 * @property {string} [linkedin_share_url] - Optional LinkedIn share URL
 */
export interface GatedResourceTemplateModel extends BaseTemplateModel {
  /** Email preview text */
  preheader?: string
  /** URL for resource icon/image */
  resource_icon_url: string
  /** Name of the gated resource */
  resource_name: string
  /** URL to access the resource */
  resource_link: string
  /** URL to access the resource preview */
  resource_preview_link?: string
  /** Canonical URL used when generating share links */
  resource_share_url?: string
  /** Description of the resource */
  resource_description: string
  /** Flexible label for the resource family (Template, Calculator results, Playbook, etc.) */
  resource_type_label?: string
  /** Flexible primary CTA copy */
  resource_cta_label?: string
  /** Flexible heading above the access button */
  resource_access_heading?: string
  /** Flexible note shown above the access button */
  resource_delivery_note?: string
  /** Flexible suggested next step copy */
  resource_usage_tip?: string
  /** Flexible suggested next step heading */
  resource_usage_heading?: string
  /** Flexible heading for related resources */
  related_resources_heading?: string
  /** Flexible social/share prompt */
  share_prompt?: string
  /** Optional URL for first related resource */
  related_resource_1_link?: string
  /** Optional name for first related resource */
  related_resource_1_name?: string
  /** Optional URL for second related resource */
  related_resource_2_link?: string
  /** Optional name for second related resource */
  related_resource_2_name?: string
  /** Optional Twitter share URL */
  twitter_share_url?: string
  /** Optional LinkedIn share URL */
  linkedin_share_url?: string
}

/**
 * Template model for resource center tool results
 * @interface
 * 
 * @property {string} email - Subscriber email address
 * @property {string} [preheader] - Email preview text
 * @property {string} tool_type - Type of assessment tool
 * @property {string} tool_name - Name of assessment tool
 * @property {string} result_summary - Summary of assessment results
 * @property {Object} [metrics] - Optional metrics data
 * @property {string} metrics.value - Metric value
 * @property {string} metrics.label - Metric label
 * @property {string} [detailed_analysis] - Optional detailed analysis
 * @property {Object} [has_visualization] - Optional visualization data
 * @property {string} [has_visualization.visualization_title] - Visualization title
 * @property {Object} [has_visualization.progress_bars] - Progress bar data
 * @property {string} has_visualization.progress_bars.label - Progress bar label
 * @property {string} has_visualization.progress_bars.value - Progress bar value
 * @property {string} has_visualization.progress_bars.percentage - Progress bar percentage
 * @property {Object} [recommendations] - Optional recommendations
 * @property {string} recommendations.title - Recommendations title
 * @property {string} recommendations.description - Recommendations description
 * @property {string} [action_text] - Optional CTA text
 * @property {Object} related_resources - Related resources data
 * @property {string} related_resources.title - Resource title
 * @property {string} related_resources.description - Resource description
 * @property {string} related_resources.link - Resource link
 * @property {string} related_resources.cta - Resource CTA text
 */
export interface ResourceCenterToolResultsModel {
  /** Subscriber email address */
  email: string
  /** Email preview text */
  preheader?: string
  /** Type of assessment tool */
  tool_type: string
  /** Name of assessment tool */
  tool_name: string
  /** Summary of assessment results */
  result_summary: string
  /** Optional metrics data */
  metrics?: {
    /** Metric value */
    value: string
    /** Metric label */
    label: string
  }
  /** Optional detailed analysis */
  detailed_analysis?: string
  /** Optional visualization data */
  has_visualization?: {
    /** Visualization title */
    visualization_title?: string
    /** Progress bar data */
    progress_bars?: {
      /** Progress bar label */
      label: string
      /** Progress bar value */
      value: string
      /** Progress bar percentage */
      percentage: string
    }
  }
  /** Optional recommendations */
  recommendations?: {
    /** Recommendations title */
    title: string
    /** Recommendations description */
    description: string
  }
  /** Optional CTA text */
  action_text?: string
  /** Related resources data */
  related_resources: {
    /** Resource title */
    title: string
    /** Resource description */
    description: string
    /** Resource link */
    link: string
    /** Resource CTA text */
    cta: string
  }
}

/**
 * Template model for minimal newsletter
 * @interface
 * 
 * @property {string} email - Subscriber email address
 */
export interface NewsletterModel {
  email: string
}

/**
 * Template model for early access application confirmation
 * @interface
 * 
 * @property {string} first_name - User's first name
 * @property {string} plan_tier - Selected plan tier (Starter, Business, Enterprise)
 * @property {string} number_of_seats - Number of seats requested
 * @property {string} monthly_total - Calculated monthly total cost
 * @property {string} company_industry - Company industry
 * @property {string} email - User's email address
 */
export interface EarlyAccessApplicationModel {
  /** User's first name */
  first_name: string
  /** Selected plan tier */
  plan_tier: string
  /** Number of seats requested */
  number_of_seats: string
  /** Calculated monthly total cost */
  monthly_total: string
  /** Company industry */
  company_industry: string
  /** User's email address */
  email: string
}

/**
 * Template model for gated content v2
 * @interface
 * 
 * @property {string} email - Subscriber email address
 * @property {string} content_name - Name of the gated resource/content (i.e. "Buyer Persona Template")
 */
export interface GatedContentNoDownloadModel {
  email: string
  content_name: string
}

/**
 * Generic template model that can be extended for specific template types.
 * Allows for any fields to be provided based on the template alias.
 * @type {Record<string, Partial<BaseTemplateModel> | Record<string, unknown> | undefined>}
 */
export type GenericTemplateModel = Record<string, Partial<BaseTemplateModel> | Record<string, unknown> | undefined>

/**
 * Flexible template configuration that can be used for any template type.
 * @interface
 * 
 * @property {string} templateAlias - Template identifier
 * @property {string} [to] - Optional email to send to (can be set by form component)
 * @property {string} [from] - Optional email to send from (can be set by form component)
 * @property {GenericTemplateModel} templateModel - Template model data with flexible structure
 */
export interface GenericTemplateConfig {
  /** Template identifier */
  templateAlias: string
  /** Template model data with flexible structure */
  templateModel: GenericTemplateModel
}

/**
 * Configuration for email templates matching Postmark server action.
 * @interface
 * 
 * @property {string} templateAlias - Template identifier
 * @property {string} to - Email to send to
 * @property {string} from - Email to send from
 * @property {Object} templateModel - Template model data
 * @property {BaseTemplateModel} [templateModel.base] - Base template data
 * @property {GatedResourceTemplateModel} [templateModel.gatedResource] - Gated resource template data
 * @property {ResourceCenterToolResultsModel} [templateModel.resourceCenterToolResults] - Tool results template data
 * @property {NewsletterMinimalModel} [templateModel.newsletterMinimal] - Minimal newsletter template data
 * @property {GatedContentNoLinkModel} [templateModel.gatedContentNoLink] - Gated content v2 template data
 * @property {Record<string, unknown>} [templateModel.custom] - Custom template data
 */
export interface EmailTemplateConfig {
  /** Template identifier */
  templateAlias: string
  /** Email to send to */
  to: string
  /** Email to send from */
  from: string
  /** Template model data */
  templateModel: {
    base?: BaseTemplateModel
    gatedResource?: GatedResourceTemplateModel
    gatedContentNoDownload?: GatedContentNoDownloadModel
    resourceCenterToolResults?: ResourceCenterToolResultsModel
    newsletter?: NewsletterModel
    earlyAccessApplication?: EarlyAccessApplicationModel
    custom?: Record<string, unknown>
  }
}

// ==============================
// CRM INTEGRATION
// ==============================

/**
 * Configuration for CRM integration matching HubSpot server action.
 * @interface
 * 
 * @property {string} [email] - Email to add/update
 * @property {string} [source] - Optional signup source
 * @property {string} [templateAlias] - Optional template alias
 * @property {string} [listId] - Optional mailing list identifier
 * @property {string[]} [tags] - Optional tags for segmentation
 * @property {Record<string, unknown>} [customFields] - Optional custom field mappings
 * @property {Object} [options] - Additional CRM-specific options
 * @property {boolean} [options.addToList] - Whether to add to list
 * @property {boolean} [options.sendWelcomeEmail] - Whether to send welcome email
 * @property {boolean} [options.doubleOptIn] - Whether to use double opt-in
 * @property {boolean} [options.updateExisting] - Whether to update existing records
 * @property {boolean} [options.retryOnFailure] - Whether to retry on failure
 * @property {number} [options.retryAttempts] - Number of retry attempts
 * @property {number} [options.retryDelay] - Delay between retries in ms
 */
export interface CRMConfig {
  /** Email to add/update */
  email?: string
  /** Optional signup source */
  source?: string
  /** Optional template alias */
  templateAlias?: string
  /** Optional mailing list identifier */
  listId?: string
  /** Optional tags for segmentation */
  tags?: string[]
  /** Optional custom field mappings */
  customFields?: Record<string, unknown>
  /** Additional CRM-specific options */
  options?: {
    addToList?: boolean
    sendWelcomeEmail?: boolean
    doubleOptIn?: boolean
    updateExisting?: boolean
    retryOnFailure?: boolean
    retryAttempts?: number
    retryDelay?: number
  }
}

// ==============================
// VALIDATION
// ==============================

/**
 * Configuration for form validation.
 * @interface
 * @property {Object} [rules] - Validation rules
 * @property {boolean} [rules.required] - Whether field is required
 * @property {boolean} [rules.format] - Whether to validate email format
 * @property {string[]} [rules.blockedDomains] - List of blocked email domains
 * @property {string[]} [rules.allowedDomains] - List of allowed email domains
 * @property {boolean} [rules.requireCorporateEmail] - Whether to require corporate email
 * @property {number} [rules.minLength] - Minimum length requirement
 * @property {number} [rules.maxLength] - Maximum length requirement
 * @property {Array<{test: (email: string) => boolean | Promise<boolean>, message: string}>} [rules.customRules] - Custom validation rules
 * @property {Object} [messages] - Custom validation messages
 * @property {string} [messages.required] - Required field message
 * @property {string} [messages.invalid] - Invalid format message
 * @property {string} [messages.blocked] - Blocked domain message
 * @property {string} [messages.corporate] - Corporate email requirement message
 * @property {Object} [timing] - Validation timing configuration
 * @property {boolean} [timing.validateOnChange] - Whether to validate on change
 * @property {boolean} [timing.validateOnBlur] - Whether to validate on blur
 * @property {boolean} [timing.validateOnSubmit] - Whether to validate on submit
 * @property {number} [timing.debounceMs] - Debounce delay in milliseconds
 */
export interface ValidationConfig {
  /** Validation rules */
  rules?: {
    required?: boolean
    format?: boolean
    blockedDomains?: string[]
    allowedDomains?: string[]
    requireCorporateEmail?: boolean
    minLength?: number
    maxLength?: number
    customRules?: Array<{
      test: (email: string) => boolean | Promise<boolean>
      message: string
    }>
  }
  /** Custom validation messages */
  messages?: {
    required?: string
    invalid?: string
    blocked?: string
    corporate?: string
    [key: string]: string | undefined
  }
  /** Validation timing configuration */
  timing?: {
    validateOnChange?: boolean
    validateOnBlur?: boolean
    validateOnSubmit?: boolean
    debounceMs?: number
  }
}

/**
 * Configuration for form theming.
 * @interface
 * @property {FormVariant} variant - Visual variant
 * @property {FormLayout} layout - Layout style
 * @property {FormSize} size - Component size
 * @property {FormTheme} theme - Color theme
 * @property {Object} [className] - Custom class names
 * @property {string} [className.container] - Container class name
 * @property {string} [className.form] - Form class name
 * @property {string} [className.input] - Input class name
 * @property {string} [className.button] - Button class name
 * @property {string} [className.error] - Error class name
 * @property {string} [className.success] - Success class name
 * @property {string} [className.icon] - Icon class name
 * @property {Object} [style] - Custom styles
 * @property {React.CSSProperties} [style.container] - Container styles
 * @property {React.CSSProperties} [style.form] - Form styles
 * @property {React.CSSProperties} [style.input] - Input styles
 * @property {React.CSSProperties} [style.button] - Button styles
 * @property {React.CSSProperties} [style.error] - Error styles
 * @property {React.CSSProperties} [style.success] - Success styles
 */
export interface ThemeConfig {
  /** Visual variant */
  variant: FormVariant
  /** Layout style */
  layout: FormLayout
  /** Component size */
  size: FormSize
  /** Color theme */
  theme: FormTheme
  /** Custom class names */
  className?: {
    container?: string
    form?: string
    input?: string
    button?: string
    error?: string
    success?: string
    icon?: string
  }
  /** Custom styles */
  style?: {
    container?: React.CSSProperties
    form?: React.CSSProperties
    input?: React.CSSProperties
    button?: React.CSSProperties
    error?: React.CSSProperties
    success?: React.CSSProperties
  }
}

/**
 * Configuration for form content and copy.
 * @interface
 * @property {Object} [title] - Form title configuration
 * @property {string} title.text - Title text
 * @property {string} [title.className] - Title class name
 * @property {Object} [subtitle] - Form subtitle configuration 
 * @property {string} subtitle.text - Subtitle text
 * @property {string} [subtitle.className] - Subtitle class name
 * @property {Object} [description] - Form description configuration
 * @property {string} description.text - Description text
 * @property {string} [description.className] - Description class name
 * @property {Object} [input] - Input field configuration
 * @property {string} [input.placeholder] - Input placeholder text
 * @property {string} [input.label] - Input label text
 * @property {string} [input.className] - Input class name
 * @property {Object} button - Submit button configuration
 * @property {string} button.text - Button text
 * @property {string} button.loadingText - Button text during loading state
 * @property {ReactNode} [button.icon] - Button icon
 * @property {string} [button.className] - Button class name
 * @property {Object} messages - Form message configuration
 * @property {string} messages.success - Success message text
 * @property {string} messages.error - Error message text
 * @property {Record<string, string>} [messages.validation] - Custom validation messages
 * @property {Object} [privacy] - Privacy notice configuration
 * @property {boolean} [privacy.enabled] - Whether to show privacy notice
 * @property {string} privacy.text - Privacy notice text
 * @property {Object} [privacy.privacyLink] - Privacy policy link
 * @property {string} privacy.privacyLink.text - Privacy policy link text
 * @property {string} privacy.privacyLink.url - Privacy policy URL
 * @property {Object} [privacy.termsLink] - Terms of service link
 * @property {string} privacy.termsLink.text - Terms of service link text
 * @property {string} privacy.termsLink.url - Terms of service URL
 * @property {string} [privacy.disclaimer] - Additional disclaimer text
 * @property {string} [privacy.className] - Privacy notice class name
 * @property {ReactNode} [customContent] - Optional custom content
 */
export interface ContentConfig {
  /** Form title configuration */
  title?: {
    text: string
    className?: string
  }
  /** Form subtitle configuration */
  subtitle?: {
    text: string
    className?: string
  }
  /** Form description configuration */
  description?: {
    text: string
    className?: string
  }
  /** Input field configuration */
  input?: {
    placeholder?: string
    label?: string
    className?: string
  }
  /** Submit button configuration */
  button: {
    text: string
    loadingText: string
    icon?: ReactNode
    className?: string
  }
  /** Form message configuration */
  messages: {
    success: string
    error: string
    validation?: Record<string, string>
  }
  /** Privacy notice configuration */
  privacy?: {
    enabled?: boolean
    text: string
    privacyLink?: {
      text: string
      url: string
    }
    termsLink?: {
      text: string
      url: string
    }
    disclaimer?: string
    className?: string
  }
  /** Optional custom content */
  customContent?: ReactNode
}

/**
 * Configuration for individual feature list items displayed in UI lists/grids.
 * This interface defines the structure of a single feature item that can be rendered
 * in feature lists, marketing sections, or product highlights. Not to be confused with
 * FeatureConfig which controls form behavior and settings.
 * 
 * @interface
 * @property {string} text - The main text/title of the feature item
 * @property {string} [description] - Optional detailed description of the feature
 * @property {React.ReactNode} [icon] - Optional icon element to display with the feature
 * @property {string} [className] - Optional CSS class name for custom styling
 */
export interface FeatureListItemConfig {
  /** Main text/title of the feature item */
  text: string
  /** Optional detailed description of the feature */
  description?: string
  /** Optional icon element to display with the feature */
  icon?: React.ReactNode
  /** Optional CSS class name for custom styling */
  className?: string
}

/**
 * Configuration for form features and behavior.
 * @interface
 * @property {Object} [ui] - UI element visibility settings
 * @property {boolean} [ui.showIcon] - Whether to show icon
 * @property {boolean} [ui.showPrivacy] - Whether to show privacy notice
 * @property {boolean} [ui.showTitle] - Whether to show title
 * @property {boolean} [ui.showDescription] - Whether to show description
 * @property {boolean} [ui.showLoadingState] - Whether to show loading state
 * @property {boolean} [ui.showSuccessMessage] - Whether to show success message
 * @property {boolean} [ui.showErrorMessage] - Whether to show error message
 * @property {boolean} [ui.embedInInput] - Whether to embed button in input
 * @property {Object} [behavior] - Form behavior settings
 * @property {boolean} [behavior.enableAutoFocus] - Whether to enable auto-focus
 * @property {boolean} [behavior.persistFormState] - Whether to persist form state
 * @property {boolean} [behavior.enableKeyboardShortcuts] - Whether to enable keyboard shortcuts
 * @property {boolean} [behavior.clearOnSuccess] - Whether to clear form on success
 * @property {boolean} [behavior.resetOnError] - Whether to reset form on error
 * @property {Object} [animation] - Animation settings
 * @property {boolean} [animation.enabled] - Whether to enable animations
 * @property {('fade' | 'slide' | 'scale')} [animation.type] - Animation type
 * @property {number} [animation.duration] - Animation duration in ms
 */
export interface FeatureConfig {
  /** UI element visibility settings */
  ui?: {
    showIcon?: boolean
    showPrivacy?: boolean
    showTitle?: boolean
    showDescription?: boolean
    showLoadingState?: boolean
    showSuccessMessage?: boolean
    showErrorMessage?: boolean
    embedInInput?: boolean
  }
  /** Form behavior settings */
  behavior?: {
    enableAutoFocus?: boolean
    persistFormState?: boolean
    enableKeyboardShortcuts?: boolean
    clearOnSuccess?: boolean
    resetOnError?: boolean
  }
  /** Animation settings */
  animation?: {
    enabled?: boolean
    type?: 'fade' | 'slide' | 'scale'
    duration?: number
  }
}

// ==============================
// ANALYTICS
// ==============================

/**
 * Configuration for analytics tracking.
 * @interface
 * @property {boolean} [enabled] - Enable/disable analytics
 * @property {string} [category] - Event category
 * @property {string} [action] - Event action
 * @property {string} [label] - Event label
 * @property {string} [elementId] - Element identifier
 * @property {string} [pageLocation] - Page location
 * @property {Record<string, string>} [customDimensions] - Custom dimensions
 */
export interface AnalyticsConfig {
  /** Enable/disable analytics */
  enabled?: boolean
  /** Event category */
  category?: string
  /** Event action */
  action?: string
  /** Event label */
  label?: string
  /** Element identifier */
  elementId?: string
  /** Page location */
  pageLocation?: string
  /** Custom dimensions */
  customDimensions?: Record<string, string>
}

// ==============================
// FORM STATE & HANDLERS
// ==============================

/**
 * Form state interface.
 * @interface
 * @property {string} email - Current email value
 * @property {string | null} error - Current error message
 * @property {boolean} isSubmitting - Form submission status
 * @property {boolean} isSuccess - Form success status
 * @property {boolean} isValid - Form validation status
 * @property {boolean} isDirty - Form modification status
 * @property {boolean} isTouched - Form interaction status
 */
export interface FormState {
  /** Current email value */
  email: string
  /** Current error message */
  error: string | null
  /** Form submission status */
  isSubmitting: boolean
  /** Form success status */
  isSuccess: boolean
  /** Form validation status */
  isValid: boolean
  /** Form modification status */
  isDirty: boolean
  /** Form interaction status */
  isTouched: boolean
}

/**
 * Form event handlers interface.
 * @interface
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => void} handleChange - Handle input change
 * @property {(e: React.FormEvent) => Promise<void>} handleSubmit - Handle form submission
 * @property {() => void} handleBlur - Handle input blur
 * @property {() => void} handleReset - Handle form reset
 * @property {() => Promise<boolean>} validate - Validate form
 */
export interface FormHandlers {
  /** Handle input change */
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Handle form submission */
  handleSubmit: (e: React.FormEvent) => Promise<void>
  /** Handle input blur */
  handleBlur: () => void
  /** Handle form reset */
  handleReset: () => void
  /** Validate form */
  validate: () => Promise<boolean>
}

/**
 * Hook return type interface that combines form state and handlers.
 * @interface
 * @extends {FormState}
 * @extends {FormHandlers}
 */
export interface UseEmailFormReturn extends FormState, FormHandlers {}

// ==============================
// FORM CONFIGURATION & PROPS
// ==============================

/**
 * Form configuration interface.
 * @interface
 * @property {Partial<ThemeConfig>} [theme] - Theme configuration
 * @property {ContentConfig} content - Content configuration
 * @property {Partial<FeatureConfig>} [features] - Feature configuration
 */
export interface FormConfig {
  /** Theme configuration */
  theme?: Partial<ThemeConfig>
  /** Content configuration */
  content: ContentConfig
  /** Feature configuration */
  features?: Partial<FeatureConfig>
}

/**
 * Render props interface.
 * @interface
 * @property {FormState} state - Form state
 * @property {FormHandlers} handlers - Form handlers
 * @property {FormConfig} config - Form configuration
 */
export interface RenderProps {
  /** Form state */
  state: FormState
  /** Form handlers */
  handlers: FormHandlers
  /** Form configuration */
  config: FormConfig
}

/**
 * Base form props interface.
 * @interface
 * @property {string} [id] - Form identifier
 * @property {string} [name] - Form name
 * @property {string} [className] - Custom class name
 * @property {React.CSSProperties} [style] - Custom styles
 * @property {EmailTemplateConfig} [template] - Email template configuration
 * @property {CRMConfig} [crm] - CRM integration configuration
 * @property {ValidationConfig} [validation] - Validation configuration
 * @property {Partial<ThemeConfig>} [theme] - Theme configuration
 * @property {ContentConfig} content - Content configuration
 * @property {Partial<FeatureConfig>} [features] - Feature configuration
 * @property {AnalyticsConfig} [analytics] - Analytics configuration
 * @property {(email: string) => Promise<void> | void} [onSubmit] - Form submission handler
 * @property {(error: Error) => void} [onError] - Error handler
 * @property {() => void} [onSuccess] - Success handler
 * @property {(email: string) => void} [onChange] - Change handler
 * @property {(isValid: boolean) => void} [onValidation] - Validation handler
 * @property {Object} [aria] - Accessibility attributes
 * @property {string} [aria.label] - ARIA label
 * @property {string} [aria.describedBy] - ARIA describedby
 * @property {boolean} [aria.required] - ARIA required
 * @property {boolean} [aria.invalid] - ARIA invalid
 * @property {ReactNode | ((props: RenderProps) => ReactNode)} [children] - Form children
 */
export interface BaseFormProps {
  /** Form identifier */
  id?: string
  /** Form name */
  name?: string
  /** Custom class name */
  className?: string
  /** Custom styles */
  style?: React.CSSProperties
  
  /** Email template configuration */
  template?: EmailTemplateConfig
  /** CRM integration configuration */
  crm?: CRMConfig
  /** Validation configuration */
  validation?: ValidationConfig
  /** Theme configuration */
  theme?: Partial<ThemeConfig>
  /** Content configuration */
  content: ContentConfig
  /** Feature configuration */
  features?: Partial<FeatureConfig>
  /** Analytics configuration */
  analytics?: AnalyticsConfig
  
  /** Form submission handler */
  onSubmit?: (email: string) => Promise<void> | void
  /** Error handler */
  onError?: (error: Error) => void
  /** Success handler */
  onSuccess?: () => void
  /** Change handler */
  onChange?: (email: string) => void
  /** Validation handler */
  onValidation?: (isValid: boolean) => void
  
  /** Accessibility attributes */
  aria?: {
    /** ARIA label for the form */
    label?: string
    /** ID of element(s) that describe the form */
    describedBy?: string
    /** Whether the form input is required */
    required?: boolean
    /** Whether the form input is invalid */
    invalid?: boolean
  }
  
  /** Form children */
  children?: ReactNode | ((props: RenderProps) => ReactNode)
}

/**
 * Configuration for privacy notice that can be reused across variants.
 * @interface
 * @property {boolean} enabled - Whether to show privacy notice
 * @property {string} text - Privacy notice text content
 * @property {Object} [privacyLink] - Privacy policy link configuration
 * @property {string} privacyLink.text - Privacy policy link text
 * @property {string} privacyLink.url - Privacy policy URL
 * @property {Object} [termsLink] - Terms of service link configuration
 * @property {string} termsLink.text - Terms of service link text
 * @property {string} termsLink.url - Terms of service URL
 * @property {string} [disclaimer] - Additional disclaimer text
 * @property {string} [className] - Custom CSS class name
 */
export interface PrivacyConfig {
  /** Whether to show privacy notice */
  enabled: boolean
  /** Privacy notice text */
  text: string
  /** Privacy policy link configuration */
  privacyLink?: {
    text: string
    url: string
  }
  /** Terms of service link configuration */
  termsLink?: {
    text: string
    url: string
  }
  /** Additional disclaimer text */
  disclaimer?: string
  /** Custom class name */
  className?: string
}

/**
 * Configuration for badges that can be reused across variants.
 * @interface
 * @property {boolean} enabled - Whether badge is enabled
 * @property {string} text - Primary badge text
 * @property {string} [subText] - Optional secondary badge text
 * @property {ReactNode} [icon] - Optional icon component
 * @property {('default' | 'success' | 'warning' | 'error' | 'new')} [variant] - Badge style variant
 * @property {string} [className] - Custom CSS class name
 */
export interface BadgeConfig {
  /** Whether badge is enabled */
  enabled: boolean
  /** Badge text */
  text: string
  /** Optional secondary text */
  subText?: string
  /** Optional icon component */
  icon?: ReactNode
  /** Badge variant */
  variant?: 'default' | 'success' | 'warning' | 'error' | 'new'
  /** Custom class name */
  className?: string
}

/**
 * Configuration for floating content that can be reused across variants.
 * @interface
 * @property {BadgeConfig} [badge] - Optional badge configuration
 * @property {Array<{label: string, value: string, icon?: ReactNode, className?: string}>} [stats] - Optional stats to display
 * @property {ReactNode} [bottomContent] - Optional bottom content
 */
export interface FloatingContentConfig {
  /** Optional badge configuration */
  badge?: BadgeConfig
  /** Optional stats to display */
  stats?: Array<{
    label: string
    value: string
    icon?: ReactNode
    className?: string
  }>
  /** Optional bottom content */
  bottomContent?: ReactNode
}

/**
 * Configuration for images that can be reused across variants.
 * @interface
 * @property {string} src - Source URL of the image
 * @property {string} [alt] - Alt text for accessibility
 * @property {string} [className] - Custom CSS class name
 * @property {('cover' | 'contain' | 'fill')} [fit] - Image object-fit property
 * @property {('center' | 'top' | 'bottom' | 'left' | 'right')} [position] - Image position/alignment
 * @property {Object} [overlay] - Optional overlay configuration
 * @property {boolean} [overlay.enabled] - Whether overlay is enabled
 * @property {string} [overlay.color] - Solid color for overlay
 * @property {number} [overlay.opacity] - Opacity level (0-1)
 * @property {string} [overlay.gradient] - CSS gradient string
 * @property {boolean} [overlay.blur] - Whether to apply blur effect
 * @property {string} [overlay.className] - Custom class name for overlay
 * @property {React.CSSProperties} [overlay.style] - Custom styles for overlay
 * @property {FloatingContentConfig} [floatingContent] - Optional floating content configuration
 */
export interface ImageConfig {
  /** Source URL of the image */
  src: string
  /** Alt text for accessibility */
  alt?: string
  /** Custom class name */
  className?: string
  /** Image object-fit property */
  fit?: 'cover' | 'contain' | 'fill'
  /** Image position/alignment */
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  /** Optional overlay configuration */
  overlay?: {
    /** Whether overlay is enabled */
    enabled?: boolean
    /** Solid color for overlay */
    color?: string
    /** Opacity level (0-1) */
    opacity?: number
    /** CSS gradient string */
    gradient?: string
    /** Whether to apply blur effect */
    blur?: boolean
    /** Custom class name for overlay */
    className?: string
    /** Custom styles for overlay */
    style?: React.CSSProperties
  }
  /** Optional floating content configuration */
  floatingContent?: FloatingContentConfig
}

/**
 * Common subtitle configuration used across variants
 */
export interface SubtitleConfig {
  /** Subtitle text content */
  text: string
  /** Optional custom class name */
  className?: string
}

/**
 * Base button configuration
 */
export interface BaseButtonConfig {
  text?: string
  loadingText?: string
  className?: string
  icon?: string | React.ReactNode | null // Allow string, ReactNode, or null for icons
}
